import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, Orbit } from "lucide-react";
import { scrollToId } from "@/lib/api";

/* Futuristic orbital selector for the product range.
   Desktop: physics-driven 3D circular carousel (auto-orbit, drag momentum,
   hover pause, sector jump pills, focal HUD panel).
   Mobile (<768px): touch-swipeable snap arc row. Reduced motion respected. */

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function RangeCard({ r, i, active, testIdPrefix = "orbital-card" }) {
    const inner = (
        <>
            <span className="range-corners" aria-hidden="true" />
            <span className="flex items-center gap-2 font-mono text-[9px] tracking-[0.25em] text-copper">
                <span className="w-3.5 h-px bg-copper-bright inline-block" />
                {String(i + 1).padStart(2, "0")}
            </span>
            <span className="flex items-end justify-between gap-2 mt-7">
                <span className={`font-display font-semibold text-[15px] leading-tight transition-colors duration-300 ${active ? "text-copper" : "text-ink"}`}>
                    {r.name}
                </span>
                <span className={`w-7 h-7 shrink-0 rounded-full border flex items-center justify-center transition-all duration-300 ${active ? "border-copper bg-copper text-white rotate-45" : "border-line text-dim"}`}>
                    <ArrowUpRight size={12} />
                </span>
            </span>
            <span className="font-mono text-[8px] tracking-[0.2em] text-dim uppercase mt-2">
                {r.to ? "Browse catalogue" : "Enquire for availability"}
            </span>
        </>
    );
    const cls = `group flex flex-col justify-between w-full h-full text-left rounded-md border p-4 transition-colors duration-300 focus:outline-none focus-visible:border-copper backdrop-blur-md ${
        active ? "border-copper/70 bg-white/90 shadow-[0_0_28px_-6px_var(--copper)]" : "border-line bg-white/70"
    }`;
    return r.to ? (
        <Link to={r.to} data-testid={`${testIdPrefix}-${slug(r.name)}`} className={cls} tabIndex={active ? 0 : -1}>
            {inner}
        </Link>
    ) : (
        <button data-testid={`${testIdPrefix}-${slug(r.name)}`} className={cls} onClick={() => scrollToId("enquiry")} tabIndex={active ? 0 : -1}>
            {inner}
        </button>
    );
}

export default function OrbitalRange({ range }) {
    const wrapRef = useRef(null);
    const cardRefs = useRef([]);
    const rotRef = useRef(0);
    const velRef = useRef(0);
    const targetRef = useRef(null);
    const dragRef = useRef(null);
    const suppressClickRef = useRef(false);
    const hoverRef = useRef(false);
    const resumeAtRef = useRef(0);
    const [front, setFront] = useState(0);
    const [radius, setRadius] = useState(420);
    const n = range.length;
    const step = 360 / n;
    const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const [speed, setSpeed] = useState(1);
    const speedRef = useRef(1);
    const AUTO = reduced ? 0 : 3.2; // deg per second (× speed multiplier)

    const jump = useCallback((idx) => {
        // rotate so item idx faces front (rot ≡ -idx*step)
        const want = -idx * step;
        const cur = ((rotRef.current % 360) + 360) % 360;
        let delta = ((want % 360) + 360) % 360 - cur;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        targetRef.current = rotRef.current + delta;
        resumeAtRef.current = performance.now() + 7000;
    }, [step]);

    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const measure = () => setRadius(Math.min(520, Math.max(120, wrap.clientWidth * (wrap.clientWidth < 768 ? 0.34 : 0.4))));
        measure();
        window.addEventListener("resize", measure);

        let raf;
        let last = performance.now();
        const tick = (now) => {
            const dt = Math.min(64, now - last) / 1000;
            last = now;
            if (dragRef.current) {
                // handled by pointermove
            } else if (targetRef.current !== null) {
                const d = targetRef.current - rotRef.current;
                rotRef.current += d * Math.min(1, dt * 7);
                if (Math.abs(d) < 0.05) targetRef.current = null;
            } else {
                rotRef.current += velRef.current * dt * 60;
                velRef.current *= Math.pow(0.94, dt * 60);
                if (Math.abs(velRef.current) < 0.002) velRef.current = 0;
                if (!hoverRef.current && now > resumeAtRef.current) rotRef.current += AUTO * speedRef.current * dt;
            }
            const rot = rotRef.current;
            let best = 0, bestZ = -2;
            for (let i = 0; i < n; i++) {
                const el = cardRefs.current[i];
                if (!el) continue;
                const a = ((i * step + rot) * Math.PI) / 180;
                const cz = Math.cos(a); // 1 = front
                const sx = Math.sin(a);
                const depth = (cz + 1) / 2; // 0..1
                if (cz > bestZ) { bestZ = cz; best = i; }
                el.style.transform = `translate(-50%,-50%) translate3d(${sx * radius}px, ${(1 - depth) * -26}px, ${(cz - 1) * radius * 0.55}px) scale(${0.58 + depth * 0.52})`;
                el.style.opacity = `${0.12 + depth * 0.88}`;
                el.style.filter = depth < 0.35 ? "blur(1px)" : "";
                el.style.zIndex = `${Math.round(depth * 100)}`;
                el.style.pointerEvents = depth > 0.55 ? "auto" : "none";
            }
            setFront((f) => (f === best ? f : best));
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);

        const down = (e) => {
            dragRef.current = { x: e.clientX ?? (e.touches && e.touches[0].clientX), moved: 0 };
            targetRef.current = null;
            resumeAtRef.current = performance.now() + 6000;
        };
        const move = (e) => {
            if (!dragRef.current) return;
            const x = e.clientX ?? (e.touches && e.touches[0].clientX);
            const dx = x - dragRef.current.x;
            dragRef.current.x = x;
            dragRef.current.moved += Math.abs(dx);
            const d = dx * 0.22;
            rotRef.current += d;
            velRef.current = d;
        };
        const up = () => {
            if (dragRef.current && dragRef.current.moved > 8) {
                suppressClickRef.current = true;
                setTimeout(() => { suppressClickRef.current = false; }, 60);
            }
            dragRef.current = null;
        };
        wrap.addEventListener("pointerdown", down);
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        // suppress accidental clicks after a drag
        const click = (e) => { if (suppressClickRef.current) { e.preventDefault(); e.stopPropagation(); } };
        wrap.addEventListener("click", click, true);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", measure);
            wrap.removeEventListener("pointerdown", down);
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", up);
            wrap.removeEventListener("click", click, true);
        };
    }, [n, step, radius, AUTO]);

    const frontItem = range[front];
    const sectors = range.map((r, i) => ({ r, i })).filter(({ r }) => r.to).slice(0, 7);

    return (
        <div data-testid="orbital-range">
            {/* sector jump pills */}
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="orbital-sectors">
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-dim flex items-center gap-2 mr-1">
                    <Orbit size={12} className="text-copper" /> Jump to sector
                </span>
                {sectors.map(({ r, i }) => (
                    <button
                        key={r.name}
                        data-testid={`orbital-pill-${slug(r.name)}`}
                        onClick={() => jump(i)}
                        className={`rounded-full border px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-all duration-300 ${i === front ? "border-copper bg-copper text-white" : "border-line text-mist hover:border-copper hover:text-copper"}`}
                    >
                        {r.name}
                    </button>
                ))}
            </div>

            {/* 3D orbital ring — all devices, drag/touch to rotate */}
            <p className="text-center font-mono text-[9px] uppercase tracking-[0.3em] text-dim mb-3 flex items-center justify-center gap-3" data-testid="orbital-hint">
                <ChevronLeft size={11} className="text-copper animate-pulse" /> drag or swipe to spin the ring <ChevronRight size={11} className="text-copper animate-pulse" />
            </p>
            <div
                ref={wrapRef}
                data-testid="orbital-ring"
                className="relative block h-[300px] md:h-[400px] lg:h-[440px] select-none cursor-grab active:cursor-grabbing overflow-hidden"
                style={{ perspective: "1200px", touchAction: "pan-y" }}
                onMouseEnter={() => { hoverRef.current = true; }}
                onMouseLeave={() => { hoverRef.current = false; resumeAtRef.current = performance.now() + 1500; }}
            >
                {/* orbit guide ellipse */}
                <div
                    aria-hidden="true"
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-copper/25"
                    style={{ width: radius * 2.05, height: radius * 0.62, transform: "translate(-50%,-50%) rotateX(72deg)", transformStyle: "preserve-3d" }}
                />
                {range.map((r, i) => (
                    <div
                        key={r.name}
                        ref={(el) => (cardRefs.current[i] = el)}
                        className="absolute left-1/2 top-1/2 w-44 h-36 will-change-transform"
                        style={{ transform: "translate(-50%,-50%)" }}
                    >
                        <RangeCard r={r} i={i} active={i === front} />
                    </div>
                ))}
            </div>

            {/* focal HUD panel + controls */}
            <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4" data-testid="orbital-focal">
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        data-testid="orbital-prev"
                        onClick={() => jump((front + 1) % n)}
                        aria-label="Previous range"
                        className="w-10 h-10 rounded-full border border-line text-mist hover:border-copper hover:text-copper transition-colors duration-300 flex items-center justify-center"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        data-testid="orbital-next"
                        onClick={() => jump((front - 1 + n) % n)}
                        aria-label="Next range"
                        className="w-10 h-10 rounded-full border border-line text-mist hover:border-copper hover:text-copper transition-colors duration-300 flex items-center justify-center"
                    >
                        <ChevronRight size={16} />
                    </button>
                    <div className="flex items-center rounded-full border border-line overflow-hidden ml-1" data-testid="orbital-speed-toggle" role="group" aria-label="Orbit speed">
                        {[0.5, 1, 2].map((s) => (
                            <button
                                key={s}
                                data-testid={`orbital-speed-${String(s).replace(".", "-")}x`}
                                onClick={() => { setSpeed(s); speedRef.current = s; }}
                                aria-pressed={speed === s}
                                className={`px-3 py-2.5 font-mono text-[9px] tracking-[0.1em] transition-colors duration-300 ${speed === s ? "bg-copper text-white" : "text-dim hover:text-copper"}`}
                            >
                                {s}×
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex-1 rounded-md border border-copper/30 bg-white/70 backdrop-blur-md px-5 py-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-copper mb-1">
                            Sector {String(front + 1).padStart(2, "0")} / {String(n).padStart(2, "0")} — in focus
                        </p>
                        <p className="font-display font-semibold text-lg text-ink leading-tight truncate" data-testid="orbital-focal-name">
                            {frontItem.name}
                        </p>
                    </div>
                    {frontItem.to ? (
                        <Link
                            to={frontItem.to}
                            data-testid="orbital-focal-open"
                            className="shrink-0 rounded-full bg-copper text-white font-mono text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-copper-bright transition-colors duration-300 flex items-center gap-2"
                        >
                            Open <ArrowUpRight size={12} />
                        </Link>
                    ) : (
                        <button
                            data-testid="orbital-focal-open"
                            onClick={() => scrollToId("enquiry")}
                            className="shrink-0 rounded-full border border-copper/50 text-copper font-mono text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-copper hover:text-white transition-colors duration-300"
                        >
                            Enquire
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
